import { useState } from 'react'
import VideoPlayer from '../components/shared/VideoPlayer'

export default function VideoDemo() {
  const [currentVideo, setCurrentVideo] = useState<string>('')
  
  const demoVideos = [
    {
      title: 'YouTube Video (Standard Format)',
      url: 'https://www.youtube.com/watch?v=LDIOZ6z4QoY',
      description: 'Test YouTube video with standard watch URL'
    },
    {
      title: 'YouTube Video (Short Format)', 
      url: 'https://youtu.be/LDIOZ6z4QoY',
      description: 'Test YouTube video with shortened URL'
    },
    {
      title: 'YouTube Video (Embed Format)',
      url: 'https://www.youtube.com/embed/LDIOZ6z4QoY',
      description: 'Test YouTube video with embed URL'
    },
    {
      title: 'Vimeo Video',
      url: 'https://vimeo.com/127834880',
      description: 'Test Vimeo video'
    },
    {
      title: 'Google Drive Video (Example)',
      url: 'https://drive.google.com/file/d/1ABCdef123_example/view',
      description: 'Example Google Drive video (replace with actual shared video)'
    }
  ]

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
      <div className="max-w-6xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
            Video Player Demo
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Test the video player with different video sources and formats.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Video List */}
          <div className="lg:col-span-1">
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                Test Videos
              </h2>
              <div className="space-y-3">
                {demoVideos.map((video, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentVideo(video.url)}
                    className={`w-full text-left p-3 rounded-lg border transition-colors ${
                      currentVideo === video.url
                        ? 'bg-rose-50 dark:bg-mauve-900/20 border-rose-300 dark:border-rose-600'
                        : 'bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-600'
                    }`}
                  >
                    <div className="font-medium text-gray-900 dark:text-white text-sm mb-1">
                      {video.title}
                    </div>
                    <div className="text-xs text-gray-600 dark:text-gray-400 mb-2">
                      {video.description}
                    </div>
                    <div className="text-xs font-mono text-gray-500 dark:text-gray-500 break-all">
                      {video.url}
                    </div>
                  </button>
                ))}
              </div>

              {/* Custom URL Input */}
              <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
                  Test Custom URL
                </h3>
                <div className="space-y-3">
                  <input
                    type="url"
                    placeholder="Enter video URL..."
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-rose-500"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        setCurrentVideo(e.currentTarget.value)
                      }
                    }}
                  />
                  <button
                    onClick={() => {
                      const input = document.querySelector('input[type="url"]') as HTMLInputElement
                      if (input.value) {
                        setCurrentVideo(input.value)
                      }
                    }}
                    className="w-full px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white rounded-md text-sm font-medium transition-colors"
                  >
                    Load Video
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Video Player */}
          <div className="lg:col-span-2">
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  Video Player
                </h2>
                {currentVideo && (
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 break-all">
                    {currentVideo}
                  </p>
                )}
              </div>
              
              <div className="aspect-video">
                {currentVideo ? (
                  <VideoPlayer
                    videoUrl={currentVideo}
                    title="Demo Video"
                    onProgress={(progress) => console.log('Progress:', progress)}
                    onComplete={() => console.log('Video completed')}
                    className="w-full h-full"
                  />
                ) : (
                  <div className="w-full h-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                    <div className="text-center">
                      <div className="text-4xl mb-4">📹</div>
                      <p className="text-gray-600 dark:text-gray-400">
                        Select a video from the list to start testing
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Info Panel */}
            <div className="mt-6 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-2">
                🔧 Video Player Features
              </h3>
              <ul className="text-sm text-blue-800 dark:text-blue-200 space-y-1">
                <li>✅ YouTube video embedding with privacy-enhanced mode</li>
                <li>✅ Vimeo video support</li>
                <li>✅ Direct video file support (MP4, WebM, etc.)</li>
                <li>✅ Custom video controls for direct files</li>
                <li>✅ Responsive design for all screen sizes</li>
                <li>✅ Progress tracking and completion detection</li>
                <li>✅ Error handling for invalid URLs</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
